"use client";
import React, { useState, useCallback, useMemo } from "react";
import Button from "antd/es/button";
import Divider from "antd/es/divider";
import Flex from "antd/es/flex";
import Input from "antd/es/input";
import Title from "antd/es/typography/Title";
import Paragraph from "antd/es/typography/Paragraph";
import Text from "antd/es/typography/Text";
import {
  formatDateLong,
  formatTime12,
  formatPrice,
} from "@openings/booking-core";
import {
  getCancellationPolicyText,
  getConfirmationRefundPolicyText,
  servicesTakeUpfrontPayment,
} from "@utils/core";
import { MemberTagBadges } from "@openings/template-components";
import { useLocale } from "@/lib/i18n";
import ICalendar from "@/app/components/ICalendar";
import { scheduleWallClockToInstant } from "@/lib/helpers/scheduleInstant";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";
import { MODAL_COMPONENT_SIZE } from "./consts";
import type { AppointmentForm } from "@/lib/hooks/useAppointment";
import InlineError from "./components/InlineError";
import styles from "./appointmentFlow.module.css";

interface AppointmentConfirmationProps {
  appointmentForm: AppointmentForm;
  appointmentId?: string;
  customerId?: string;
  onDone: () => void;
}

const AppointmentConfirmation: React.FC<AppointmentConfirmationProps> = ({
  appointmentForm,
  appointmentId,
  customerId,
  onDone,
}) => {
  const {
    services,
    date,
    time,
    timezone,
    stylistName,
    scheduleAddress,
    memberTags,
  } = appointmentForm;
  // Services were run through `applyMemberPricing` before booking, so this
  // is the provider's own (cloned) description. Prefer the option's copy.
  const serviceDescriptions = services
    .map((service) =>
      (service.option?.description || service.description || "").trim(),
    )
    .filter(Boolean);

  const { t, locale } = useLocale();
  const isJa = locale === "ja";

  /* ── Notify an embedding parent (e.g. the check-in kiosk iframe) that the
     booking completed, so it can celebrate and auto-reset. No-op when not
     embedded.

     The payload carries no appointment or customer id: the web app sets no
     `frame-ancestors`, so any site can frame a booking page, and the
     appointment id still works as a capability on public routes
     (`BF-DRIFT-001`). The kiosk only reads `type`, so a bare completion
     event is all an embedder needs (booking-flow standard § 10,
     `BF-DRIFT-009`). ── */
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: "openings:booking-complete" }, "*");
    }
  }, []);

  /* ── Note ── */
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  const handleSaveNote = useCallback(async () => {
    if (!appointmentId || !customerId || !noteText.trim()) return;
    setNoteSaving(true);
    setNoteError(null);
    try {
      await bookingFlowApi.addAppointmentNotePublic({
        appointmentId,
        note: noteText.trim(),
        customerId,
      });
      setNoteSaved(true);
    } catch {
      setNoteError(t.noteSaveFailed);
    } finally {
      setNoteSaving(false);
    }
  }, [appointmentId, customerId, noteText, t.noteSaveFailed]);

  /* ── Calendar event ── */
  const calendarEvent = useMemo(() => {
    const totalMinutes = services.reduce((acc, s) => {
      const dur = s.option?.duration ?? s.duration ?? 0;
      return acc + dur;
    }, 0);
    // `date` and `time` are the schedule's wall clock, so the calendar event
    // has to be built in the schedule's zone. Reading them in the customer's
    // device zone put the event hours off for anyone booking from another
    // zone (BF-DRIFT-016). Without a zone, keep the device-zone reading.
    const dateTimeStr = `${date}T${time}:00`;
    const startDate =
      scheduleWallClockToInstant(date, time, timezone) ?? new Date(dateTimeStr);
    if (Number.isNaN(startDate.getTime())) return null;
    const startIso = startDate.toISOString();
    const endMs =
      startDate.getTime() + (totalMinutes > 0 ? totalMinutes : 60) * 60_000;
    const endIso = new Date(endMs).toISOString();
    const serviceSummary = services.map((s) => s.title).join(", ");
    const descParts = [
      serviceSummary ? `Services: ${serviceSummary}` : null,
      scheduleAddress ? `Where: ${scheduleAddress}` : null,
      stylistName ? `Provider: ${stylistName}` : null,
    ].filter(Boolean);
    return {
      title: "Appointment",
      description: descParts.join("\n"),
      startTime: startIso,
      endTime: endIso,
      location: scheduleAddress ?? "",
    };
  }, [services, date, time, timezone, scheduleAddress, stylistName]);

  /* ── Cancellation policy ── */
  const policyEnabled = appointmentForm.cancellationPolicyEnabled !== false;
  const policyText = policyEnabled
    ? getCancellationPolicyText(
        appointmentForm.cancellationPolicyHours,
        appointmentForm.cancellationPolicyNote,
        appointmentForm.lateGraceMinutes,
      )
    : null;

  // booking.confirmation.refundPolicy: the same catalog-based fact the
  // template review page shows, so every completion mode matches (§ 10).
  const refundPolicyText = servicesTakeUpfrontPayment(services)
    ? getConfirmationRefundPolicyText({
        policyEnabled: appointmentForm.cancellationPolicyEnabled,
        policyHours: appointmentForm.cancellationPolicyHours,
      })
    : null;

  return (
    <main className={styles.confirmation}>
      <Title level={3}>{t.youreBooked}</Title>
      <Paragraph>{t.appointmentConfirmed}</Paragraph>

      {/* Cancellation policy */}
      {policyText?.split("\n").map((line: string, i: number) =>
        line.trim() ? (
          <Paragraph key={i}>
            <Text>{line}</Text>
          </Paragraph>
        ) : null,
      )}

      {refundPolicyText && (
        <Paragraph>
          <Text>{refundPolicyText}</Text>
        </Paragraph>
      )}

      {/* Note section */}
      {appointmentId && customerId && (
        <>
          <Divider dashed />
          <section
            aria-labelledby="appointment-note-heading"
            data-appointment-note-section
          >
            <Title level={5} id="appointment-note-heading">
              {t.note}
            </Title>
            {!showNote && (
              <Button block onClick={() => setShowNote(true)}>
                {t.leaveNote}
              </Button>
            )}
            {showNote && (
              <Flex
                vertical
                gap={8}
                className={styles.noteForm}
                data-appointment-note-form
              >
                <Input.TextArea
                  aria-label={t.note}
                  placeholder={t.addDetails}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                  maxLength={500}
                  readOnly={noteSaving || noteSaved}
                />
                <InlineError message={noteError} />
                <Button
                  type={noteSaved ? "default" : "primary"}
                  size={MODAL_COMPONENT_SIZE}
                  loading={noteSaving}
                  disabled={noteSaved || !noteText.trim()}
                  onClick={handleSaveNote}
                  block
                >
                  {noteSaved ? t.noteSaved : t.saveNote}
                </Button>
                <span
                  className={styles.statusAnnouncement}
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {noteSaved ? t.noteSaved : ""}
                </span>
              </Flex>
            )}
          </section>
        </>
      )}

      <Divider dashed />

      <Title level={5}>{t.details}</Title>

      <Paragraph>
        <Text strong>{t.at}</Text>
        <br />
        <Text>
          {formatDateLong(date, isJa ? "ja-JP" : undefined)},{" "}
          {formatTime12(time, isJa ? { use24Hour: true } : undefined)}
        </Text>
      </Paragraph>

      {scheduleAddress && (
        <Paragraph>
          <Text strong>{t.address}</Text>
          <br />
          <Text>{scheduleAddress}</Text>
        </Paragraph>
      )}

      {stylistName && (
        <Paragraph>
          <Text strong>{t.provider}</Text>
          <br />
          <Text>{stylistName}</Text>
          <MemberTagBadges tags={memberTags} label={t.memberNotes} />
        </Paragraph>
      )}

      <Paragraph>
        <Text strong>{t.services}</Text>
        <br />
        {services.map((service) => {
          const optionPrice = service.option?.price;
          const hasOptionPrice =
            typeof optionPrice === "number" && optionPrice > 0;
          const basePrice = service.price;
          const price = hasOptionPrice
            ? optionPrice
            : typeof basePrice === "number" && basePrice > 0
              ? basePrice
              : null;
          const priceSuffix = !hasOptionPrice && service.up ? "+" : "";
          const optionLabel = service.option?.title
            ? ` (${service.option.title})`
            : "";
          return (
            <Paragraph key={service.id} style={{ marginBottom: 4 }}>
              <Text>
                {service.title}
                {optionLabel}
              </Text>
              {price ? (
                <>
                  <Text>
                    <Divider vertical /> {formatPrice(price)}
                    {priceSuffix}
                  </Text>
                </>
              ) : null}
            </Paragraph>
          );
        })}
      </Paragraph>

      {serviceDescriptions.length > 0 && (
        <Paragraph>
          <Text strong>{t.description}</Text>
          <br />
          {serviceDescriptions.map((description, i) => (
            <Paragraph
              key={`description-${i}`}
              style={{ marginBottom: 4, whiteSpace: "pre-line" }}
            >
              <Text>{description}</Text>
            </Paragraph>
          ))}
        </Paragraph>
      )}

      {/* Calendar */}
      {calendarEvent && (
        <>
          <Divider dashed />
          <Title level={5}>{t.saveToCalendar}</Title>
          <ICalendar event={calendarEvent} />
        </>
      )}

      <Divider dashed />

      <Button type="primary" size={MODAL_COMPONENT_SIZE} block onClick={onDone}>
        {t.done}
      </Button>
    </main>
  );
};

export default AppointmentConfirmation;
